/**
 * Шторки дополнительных услуг в новой теме. Бронь стола, вызов официанта,
 * отзыв и предпочтения раньше были отдельными страницами: гость уходил из
 * меню и терял место, на котором остановился. Тела этих экранов переехали
 * в компоненты, а здесь они показываются шторкой — как язык, корзина и
 * карточка блюда. Прямые ссылки на страницы продолжают работать: заведения
 * дают их в рассылках и на визитках.
 */
import React from 'react'
import { PropsWithChildren } from 'react'
import { useAppContext } from '@/pages/_app'
import { useVenueContext } from '@/services/venue'
import { uiEvents, useStateFromUiEvents, useDefaultObservable } from '@/utils/event'
import { windowStorage } from '@/utils/env'
import { VenueBottomButton } from '@/modern/components/bottom-button'
import { ModernSheet } from './modern-sheet'

export type VasSheetKind = 'bookTable' | 'callWaiter' | 'feedback' | 'preferences'

declare global {
  interface AppUIEventMap {
    VasSheetOpenClick: {
      type: 'VasSheetOpenClick'
      kind: VasSheetKind
    }
    VasSheetCloseClick: {
      type: 'VasSheetCloseClick'
    }
  }
}

/**
 * Кнопки экрана: на странице это закреплённая снизу кнопка, в шторке —
 * обычный блок в её теле, иначе он оказался бы под шторкой.
 */
export function VasActions(props: PropsWithChildren<{ asSheet?: boolean }>) {
  if (props.asSheet) {
    // «Назад» после успешной отправки — ссылка на меню, на котором гость и
    // так стоит: шторка оставалась открытой в памяти и при следующем
    // открытии показывала тот же экран успеха вместо формы. В шторке ссылка
    // закрывает шторку.
    return (
      <div
        className="vas-actions"
        onClickCapture={(event) => {
          if ((event.target as HTMLElement).closest('a')) {
            event.preventDefault()
            uiEvents.next({ type: 'VasSheetCloseClick' })
          }
        }}
      >
        {props.children}
      </div>
    )
  }
  return <VenueBottomButton>{props.children}</VenueBottomButton>
}

export function useVasSheet() {
  const state$ = useStateFromUiEvents<VasSheetKind | null>({
    id: 'vasSheet',
    startingValue: null,
    storage: windowStorage,
    process: (source, scan) => {
      return source.pipe(
        scan((state, event) => {
          switch (event.type) {
            case 'VasSheetOpenClick':
              return event.kind
            case 'VasSheetCloseClick':
              return null
          }
          return state
        }),
      )
    },
  })

  return useDefaultObservable(state$)
}

export function ModernVasSheet(props: {
  kind: VasSheetKind
  title: string
  children: React.ReactNode
}) {
  const openKind = useVasSheet()

  return (
    <ModernSheet
      title={props.title}
      isOpen={openKind === props.kind}
      tall
      onClose={() => {
        uiEvents.next({ type: 'VasSheetCloseClick' })
      }}
    >
      {openKind === props.kind ? props.children : null}
    </ModernSheet>
  )
}

/** Все шторки услуг разом — их место рядом с меню. */
export function ModernVasSheets(props: {
  bookTable?: React.ReactNode
  callWaiter?: React.ReactNode
  feedback?: React.ReactNode
  preferences?: React.ReactNode
}) {
  const { t } = useAppContext()
  const venue = useVenueContext()?.venue

  return (
    <>
      {!!props.bookTable && (
        <ModernVasSheet kind="bookTable" title={t('bookTable:bookTable')}>
          {props.bookTable}
        </ModernVasSheet>
      )}
      {!!props.callWaiter && (
        <ModernVasSheet kind="callWaiter" title={t('callTheWaiter:callWaiter')}>
          {props.callWaiter}
        </ModernVasSheet>
      )}
      {!!props.feedback && (
        // заголовок шторки — та же подпись, что у кнопки, которая её открыла
        <ModernVasSheet
          kind="feedback"
          title={venue?.features.vas?.feedback?.buttonCaption || t('common:sendUsMessage')}
        >
          {props.feedback}
        </ModernVasSheet>
      )}
      {!!props.preferences && (
        <ModernVasSheet kind="preferences" title={t('preferences:myPreferences')}>
          {props.preferences}
        </ModernVasSheet>
      )}
    </>
  )
}
